import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { ShieldCheck, Eye, Database, UserCheck, Globe, Lock, Mail } from 'lucide-react';

const DatenschutzPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50/50">
      <section className="relative pt-48 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-blue-900 mb-6">
              Datenschutzerklärung
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-700 mx-auto"></div>
          </div>

          {/* Verantwortliche Stelle */}
          <Card className="border-blue-100 bg-white/80 backdrop-blur-sm shadow-xl mb-8">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-4">Verantwortliche Stelle</h2>
                  <div className="space-y-2 text-slate-700">
                    <p><strong>Vivalo GmbH</strong></p>
                    <p>Chaltenbodenstrasse 26</p>
                    <p>8834 Schindellegi, Schweiz</p>
                    <p>Telefon: <a href="tel:+41416776767" className="text-blue-600 hover:text-blue-700">041 677 67 67</a></p>
                    <p>E-Mail: <a href="mailto:info@vivalo.ch" className="text-blue-600 hover:text-blue-700">info@vivalo.ch</a></p>
                  </div>
                  <p className="text-slate-700 mt-4">
                    Die verantwortliche Stelle entscheidet über die Zwecke und Mittel der Bearbeitung von Personendaten. Bei Fragen zum Datenschutz können Sie sich jederzeit an uns wenden.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Grundsätze */}
          <Card className="border-blue-100 bg-white/80 backdrop-blur-sm shadow-xl mb-8">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-4">Grundsätze der Datenbearbeitung</h2>
                  <div className="space-y-3 text-slate-700">
                    <p>
                      Der Schutz Ihrer Personendaten ist uns ein wichtiges Anliegen. Wir bearbeiten Ihre Daten im Einklang mit dem <strong>Schweizer Bundesgesetz über den Datenschutz (DSG)</strong> sowie der <strong>Verordnung über den Datenschutz (DSV)</strong>.
                    </p>
                    <p>
                      Personendaten werden von uns nur so weit und so lange bearbeitet, wie dies für die jeweiligen Zwecke erforderlich ist. Wir halten uns an die Grundsätze der Rechtmässigkeit, Verhältnismässigkeit und Zweckbindung.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Erhobene Daten */}
          <Card className="border-blue-100 bg-white/80 backdrop-blur-sm shadow-xl mb-8">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Database className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-4">Welche Daten wir erheben</h2>
                  <div className="space-y-4 text-slate-700">
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Kontaktformular</h3>
                      <p>
                        Wenn Sie unser Kontaktformular nutzen, erheben wir folgende Daten: Name, E-Mail-Adresse, Telefonnummer (freiwillig) und Ihre Nachricht. Diese Daten werden ausschliesslich zur Bearbeitung Ihrer Anfrage verwendet und per E-Mail an uns weitergeleitet. Die Übermittlung erfolgt über den Dienst <strong>Web3Forms</strong> (web3forms.com).
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Terminbuchung</h3>
                      <p>
                        Für die Online-Terminvereinbarung nutzen wir den externen Dienst <strong>Calendly</strong> (calendly.com). Dabei werden die von Ihnen eingegebenen Daten (Name, E-Mail, gewünschter Termin) direkt von Calendly verarbeitet. Es gelten die Datenschutzbestimmungen von Calendly.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Automatisch erhobene Daten</h3>
                      <p>
                        Beim Besuch unserer Website werden automatisch technische Daten erfasst, die Ihr Browser übermittelt. Dazu gehören: IP-Adresse, Browsertyp und -version, verwendetes Betriebssystem, Datum und Uhrzeit des Zugriffs. Diese Daten werden nicht mit anderen Datenquellen zusammengeführt und dienen ausschliesslich der technischen Bereitstellung der Website.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Zweck der Bearbeitung */}
          <Card className="border-blue-100 bg-white/80 backdrop-blur-sm shadow-xl mb-8">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Eye className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-4">Zweck der Datenbearbeitung</h2>
                  <div className="space-y-2 text-slate-700">
                    <p>Wir bearbeiten Ihre Personendaten für folgende Zwecke:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Beantwortung und Bearbeitung Ihrer Kontaktanfragen</li>
                      <li>Durchführung von Beratungsgesprächen und Offertenerstellungen</li>
                      <li>Vermittlung und Verwaltung von Versicherungs- und Vorsorgelösungen</li>
                      <li>Erfüllung gesetzlicher und regulatorischer Pflichten (z.B. FINMA-Auflagen)</li>
                      <li>Technische Bereitstellung und Sicherheit der Website</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weitergabe an Dritte */}
          <Card className="border-blue-100 bg-white/80 backdrop-blur-sm shadow-xl mb-8">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Globe className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-4">Weitergabe an Dritte</h2>
                  <div className="space-y-3 text-slate-700">
                    <p>
                      Wir geben Ihre Personendaten nur weiter, wenn dies für die Erbringung unserer Dienstleistungen erforderlich ist oder eine gesetzliche Pflicht besteht.
                    </p>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Versicherungsgesellschaften</h3>
                      <p>
                        Im Rahmen unserer Tätigkeit als Versicherungsbroker übermitteln wir Ihre Daten an die jeweiligen Versicherungsgesellschaften, soweit dies für die Offertenerstellung, den Vertragsabschluss oder die Schadensbearbeitung erforderlich ist.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Technische Dienstleister</h3>
                      <p>Für den Betrieb dieser Website setzen wir folgende Dienstleister ein:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                        <li><strong>Web3Forms</strong> (Übermittlung von Kontaktformulardaten per E-Mail)</li>
                        <li><strong>Calendly</strong> (Online-Terminvereinbarung)</li>
                        <li><strong>Cloudflare</strong> (Content Delivery Network und DDoS-Schutz)</li>
                      </ul>
                      <p className="mt-2">Diese Dienstleister können Daten ausserhalb der Schweiz verarbeiten. Wir stellen sicher, dass ein angemessenes Datenschutzniveau gewährleistet ist.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Betroffenenrechte */}
          <Card className="border-blue-100 bg-white/80 backdrop-blur-sm shadow-xl mb-8">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <UserCheck className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-4">Ihre Rechte</h2>
                  <div className="space-y-3 text-slate-700">
                    <p>Gemäss dem Schweizer Datenschutzgesetz (DSG) stehen Ihnen folgende Rechte zu:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Auskunftsrecht:</strong> Sie können jederzeit Auskunft über die von uns bearbeiteten Personendaten verlangen.</li>
                      <li><strong>Berichtigungsrecht:</strong> Sie können die Berichtigung unrichtiger Daten verlangen.</li>
                      <li><strong>Löschungsrecht:</strong> Sie können die Löschung Ihrer Daten verlangen, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.</li>
                      <li><strong>Datenherausgabe:</strong> Sie haben das Recht, Ihre Daten in einem gängigen elektronischen Format zu erhalten.</li>
                      <li><strong>Widerspruchsrecht:</strong> Sie können der Bearbeitung Ihrer Daten jederzeit widersprechen.</li>
                    </ul>
                    <p className="mt-3">
                      Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: <a href="mailto:info@vivalo.ch" className="text-blue-600 hover:text-blue-700 font-medium">info@vivalo.ch</a>
                    </p>
                    <p>
                      Sollten Sie der Ansicht sein, dass die Bearbeitung Ihrer Daten gegen das Datenschutzrecht verstösst, haben Sie das Recht, beim <strong>Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten (EDÖB)</strong> Beschwerde einzureichen.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cookies & Sicherheit */}
          <Card className="border-blue-100 bg-white/80 backdrop-blur-sm shadow-xl mb-8">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-4">Cookies & Datensicherheit</h2>
                  <div className="space-y-3 text-slate-700">
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Cookies</h3>
                      <p>
                        Diese Website verwendet ausschliesslich technisch notwendige Cookies, die für den Betrieb der Website erforderlich sind. Es werden keine Tracking- oder Analyse-Cookies eingesetzt. Ihre Einwilligung ist für diese Cookies nicht erforderlich.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Datensicherheit</h3>
                      <p>
                        Wir setzen angemessene technische und organisatorische Massnahmen ein, um Ihre Daten vor unbefugtem Zugriff, Verlust oder Missbrauch zu schützen. Die Datenübertragung erfolgt verschlüsselt über SSL/TLS.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Aufbewahrungsdauer</h3>
                      <p>
                        Wir bewahren Personendaten nur so lange auf, wie es für die Erfüllung der jeweiligen Zwecke erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen. Für Versicherungsbroker gelten in der Schweiz Aufbewahrungsfristen von bis zu 10 Jahren für geschäftsrelevante Unterlagen.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Änderungen */}
          <Card className="border-blue-100 bg-white/80 backdrop-blur-sm shadow-xl mb-8">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-4">Änderungen dieser Datenschutzerklärung</h2>
                  <p className="text-slate-700">
                    Wir behalten uns vor, diese Datenschutzerklärung jederzeit anzupassen. Die aktuelle Fassung ist stets auf unserer Website verfügbar. Es gilt jeweils die zum Zeitpunkt der Nutzung veröffentlichte Fassung.
                  </p>
                  <p className="text-slate-500 mt-4 text-sm">Stand: Mai 2026</p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
};

export default DatenschutzPage;
